import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Multi-select status filter persisted in the URL query string.
 *
 * Storing the selection in the URL (as repeated `?status=` params) means it
 * survives both navigating into a detail view and back, AND a full reload —
 * admins no longer have to re-apply "show only open + in progress" every time
 * they work through a queue. An empty selection means "all".
 */
export function useStatusFilterParam(): {
  statuses: string[];
  setStatuses: (next: string[]) => void;
  toggleStatus: (value: string) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // searchParams is referentially stable per URL, so this array is too — safe
  // to use directly in effect/callback dependency lists.
  const statuses = useMemo(() => searchParams.getAll('status'), [searchParams]);

  const setStatuses = useCallback(
    (next: string[]) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('status');
          next.forEach((s) => p.append('status', s));
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleStatus = useCallback(
    (value: string) => {
      setStatuses(
        statuses.includes(value) ? statuses.filter((s) => s !== value) : [...statuses, value],
      );
    },
    [statuses, setStatuses],
  );

  return { statuses, setStatuses, toggleStatus };
}
